import type { EventRoom, FormBrandedCustomizations, RegistrationPage } from '~/graphql';
import type { Nilable } from '~/utilities/type-guards';

// These are values that should be stored in the Brand
// but are not directly set by the Brand UI,
// nor surfaced in any of the Page-Level editors
export type GeneratedEventBrandValues = {
  backgroundColor: string;
  backgroundGradientColor: string;
  brandDarkBackgroundColor: string;
  brandLightBackgroundColor: string;
  mainTextColor: string;
  headingTextColor: string;
  playerColor: string;
  primaryTextColor: string;
  borderColor: string;
  speakerTileBackgroundColor: string;
  speakerTileBorder: string;
  speakerTileTextColor: string;
  speakerTileShadowColor: string;
  speakerTileTitleTextColor: string;
  speakerSocialStrokeColor: string;
  speakerSocialFillColor: string;
  primaryButtonBackgroundColor: string;
  primaryButtonTextColor: string;
  primaryButtonBorderColor: string;
  primaryButtonHoverBackgroundColor: string;
  primaryButtonHoverTextColor: string;
  secondaryButtonTextColor: string;
  secondaryButtonBorderColor: string;
  secondaryButtonHoverBackgroundColor: string;
  secondaryButtonHoverTextColor: string;
  slightAccentedBackgroundColor: string;
  slightAccentedTextColor: string;
  slightAccentedShadowColor: string;
  slightAccentedBorderColor: string;
  heavyAccentedBackgroundColor: string;
  heavyAccentedTextColor: string;
  heavyAccentedBorderColor: string;
  subtitleFontSize: number;
};

export type EventBrandValuesSet = GeneratedEventBrandValues & {
  backgroundColor?: string;
  borderRadius: number;
  buttonBorderRadius: Nilable<number>;
  containerBorderRadius: Nilable<number>;
  darkLogoBakeryHashedId: string;
  darkLogoUrl: string;
  hasPoweredByWistiaBadge: boolean;
  inputBorderRadius: Nilable<number>;
  isDarkMode: boolean;
  lightLogoBakeryHashedId: string;
  lightLogoUrl: string;
  logoHeight: number;
  logoTargetUrl: Nilable<string>;
  primaryColor: string;
  secondaryFont: string;
  secondaryFontSize: number;
  secondaryFontUrl?: string;
  titleFont: string;
  titleFontSize: number;
  titleFontUrl?: string;
  shouldShowLogo: boolean;
};

export type NonGeneratedEventBrandValues = Omit<
  EventBrandValuesSet,
  keyof GeneratedEventBrandValues
> & { backgroundColor?: string };

export const generatedEventBrandValuesKeys = [
  'backgroundGradientColor',
  'mainTextColor',
  'headingTextColor',
  'primaryTextColor',
  'borderColor',
  'playerColor',
  'speakerTileBackgroundColor',
  'speakerTileBorder',
  'speakerTileTextColor',
  'speakerTileShadowColor',
  'speakerTileTitleTextColor',
  'speakerSocialStrokeColor',
  'speakerSocialFillColor',
  'primaryButtonBackgroundColor',
  'primaryButtonTextColor',
  'primaryButtonBorderColor',
  'primaryButtonHoverBackgroundColor',
  'primaryButtonHoverTextColor',
  'secondaryButtonTextColor',
  'secondaryButtonBorderColor',
  'secondaryButtonHoverBackgroundColor',
  'secondaryButtonHoverTextColor',
  'brandDarkBackgroundColor',
  'brandLightBackgroundColor',
  'slightAccentedBackgroundColor',
  'slightAccentedTextColor',
  'slightAccentedShadowColor',
  'slightAccentedBorderColor',
  'heavyAccentedBackgroundColor',
  'heavyAccentedTextColor',
  'heavyAccentedBorderColor',
  'subtitleFontSize',
] satisfies (keyof GeneratedEventBrandValues)[];

export type SavedEventBrandValues = Omit<
  EventBrandValuesSet,
  keyof typeof generatedEventBrandValuesKeys
>;

export type PageLevelCustomizations = Omit<
  EventRoom | FormBrandedCustomizations | RegistrationPage,
  '__typename'
>;

export type PersistedPageLevelCustomizations<T extends PageLevelCustomizations> =
  T extends EventRoom
    ? EventRoom
    : T extends RegistrationPage
      ? RegistrationPage
      : FormBrandedCustomizations;

export type PageLevelCustomizationsInheritedFromBrand<T extends PageLevelCustomizations> =
  T extends EventRoom
    ? EventRoomCustomizationsInheritedFromBrand
    : T extends RegistrationPage
      ? RegistrationCustomizationsInheritedFromBrand
      : FormCustomizationsInheritedFromBrand;

export type PageToBrandValuesMapItem = {
  mappedBrandKey: keyof EventBrandValuesSet;
  alternateDefaultValue?: boolean | number | string;
};

export type PageToBrandValuesMap<T extends PageLevelCustomizations> = Record<
  keyof PageLevelCustomizationsInheritedFromBrand<T> | keyof PersistedPageLevelCustomizations<T>,
  PageToBrandValuesMapItem
>;

// This is a list of properties of the event room that should be
// mapped to keys from the event brand.
export const EventRoomMappedBrandValues = {
  playerBackgroundColor: {
    mappedBrandKey: 'slightAccentedBackgroundColor',
  },
  sidebarBackgroundColor: {
    mappedBrandKey: 'slightAccentedBackgroundColor',
  },
} as PageToBrandValuesMap<EventRoom>;

export type EventRoomCustomizationsInheritedFromBrand = EventBrandValuesSet &
  EventRoom & {
    playerBackgroundColor: string;
    sidebarBackgroundColor: string;
  };

export type EventRoomCustomizationsGeneratedFromBrand = EventBrandValuesSet &
  EventRoom &
  OnTheFlyEventRoomCustomizations;

type OnTheFlyEventRoomCustomizations = {
  sidebarBorderColor: string;
  sidebarTextColor: string;
  sidebarTabDotColor: string;
  sidebarShadowColor: string;
  myChatMessageBackgroundColor: string;
  myChatMessageTextColor: string;
  myChatMessageQuotedBorderColor: string;
  myChatMessageLinkPreviewBackgroundColor: string;
  myChatMessageLinkPreviewTextColor: string;
  notMyChatMessageBackgroundColor: string;
  notMyChatMessageHoverBackgroundColor: string;
  notMyChatMessageHoverTextColor: string;
  notMyChatMessageTextColor: string;
  notMyChatMessageLinkPreviewBackgroundColor: string;
  notMyChatMessageLinkPreviewTextColor: string;
  chatActionButtonIconColor: string;
  chatActionButtonHoverBackgroundColor: string;
  chatReactionBorderColor: string;
  chatReactionCountTextColor: string;
  chatSelectedReactionBackgroundColor: string;
  chatSelectedReactionTextColor: string;
  sidebarParticipantActionsTextColor: string;
  sidebarPrimaryBackgroundColor: string;
  sidebarPrimaryTextColor: string;
  sidebarPrimaryHoverBackgroundColor: string;
  sidebarPrimaryHoverTextColor: string;
  chatInputBackgroundColor: string;
  chatInputBorderColor: string;
  chatInputTextColor: string;
  chatInputIconColor: string;
  chatInputPlaceholderColor: string;
  lowEmphasisTextColor: string;
  sidebarLowEmphasisTextColor: string;
  chatSendButtonBackgroundColor: string;
  chatSendButtonTextColor: string;
  chatSendButtonBorderColor: string;
  chatSendButtonHoverBackgroundColor: string;
  chatFailedMessageBackgroundColor: string;
  chatFailedMessageTextColor: string;
  sidebarSecondaryButtonBackgroundColor: string;
  sidebarSecondaryButtonTextColor: string;
  sidebarSecondaryButtonBorderColor: string;
  sidebarSecondaryButtonHoverBackgroundColor: string;
  sidebarHeavyAccentedBackgroundColor: string;
  sidebarHeavyAccentedTextColor: string;
  sidebarHeavyAccentedLowEmphasisTextColor: string;
  sidebarHeavyAccentedHoverBackgroundColor: string;
  sidebarHeavyAccentedHoverTextColor: string;
  sidebarHeavyAccentedErrorBorderColor: string;
  sidebarHeavyAccentedErrorTextColor: string;
  sidebarSlightAccentedBackgroundColor: string;
  sidebarSlightAccentedTextColor: string;
  sidebarSlightAccentedLowEmphasisTextColor: string;
  sidebarSlightAccentedBorderColor: string;
  sidebarSlightAccentedHoverBackgroundColor: string;
  sidebarSlightAccentedHoverTextColor: string;
  sidebarSlightAccentedErrorBorderColor: string;
  sidebarSlightAccentedErrorTextColor: string;
  onStagePrimaryBackgroundColor: string;
  onStagePollBarBackgroundColor: string;
  onStageBadgeTextColor: string;
  pollSelectedBackgroundColor: string;
  pollSelectedTextColor: string;
  pollSelectedHoverBackgroundColor: string;
  pollSelectedHoverTextColor: string;
  pollUserVotedForThisOptionBackgroundColor: string;
  pollUserVotedForThisOptionTextColor: string;
  participantButtonBackgroundColor: string;
  participantButtonTextColor: string;
  participantButtonHoverBackgroundColor: string;
  participantButtonHoverTextColor: string;
  stageHeavyAccentedBackgroundColor: string;
  stageHeavyAccentedTextColor: string;
  stageHeavyAccentedLowEmphasisTextColor: string;
  stageHeavyAccentedHoverBackgroundColor: string;
  stageHeavyAccentedHoverTextColor: string;
  stageSlightAccentedBackgroundColor: string;
  stageSlightAccentedTextColor: string;
  stageSlightAccentedLowEmphasisTextColor: string;
  stageSlightAccentedHoverBackgroundColor: string;
  stageSlightAccentedHoverTextColor: string;
  goLiveButtonBackgroundColor: string;
  goLiveButtonTextColor: string;
  goLiveButtonHoverBackgroundColor: string;
  goLiveButtonHoverTextColor: string;
  audiencePlayerColor: string;
};

export type SavedRegistrationCustomizations = RegistrationPage;

export type RegistrationCustomizationsWithGeneratedValues = EventBrandValuesSet &
  OnTheFlyRegistrationCustomizations &
  RegistrationPage & {
    heroVideoPlayerColor: string;
    heroVideoLetterboxColor: string;
  };

// This is a list of properties of the registration page that should be
// mapped to keys from the event brand.
export const RegistrationPageMappedBrandValues = {
  heroVideoPlayerColor: {
    mappedBrandKey: 'primaryColor',
  },
  heroVideoLetterboxColor: {
    mappedBrandKey: 'slightAccentedBackgroundColor',
    alternateDefaultValue: '#000000',
  },
} as PageToBrandValuesMap<RegistrationPage>;

export type RegistrationCustomizationsInheritedFromBrand = EventBrandValuesSet &
  RegistrationPage & {
    heroVideoPlayerColor: string;
    heroVideoLetterboxColor: string;
  };

type OnTheFlyRegistrationCustomizations = {
  dateTimeBackgroundColor: string;
  dateTimeTextColor: string;
  formBackgroundColor: string;
  formTextColor: string;
};

export type FormCustomizationsInheritedFromBrand = EventBrandValuesSet &
  FormBrandedCustomizations & {
    formBackgroundColor: string;
    buttonColor: string;
    inputLabelFont: string;
    inputFont: string;
    buttonFont: string;
    inputBorderColor: string;
    pageBackgroundColor: string;
    titleFontSize: number;
  };

export type FormCustomizationsWithGeneratedValues = Omit<FormBrandedCustomizations, '__typename'> &
  OnTheFlyFormBrandedCustomizations;

export const FormCustomizationsMappedBrandValues = {
  formBackgroundColor: {
    mappedBrandKey: 'slightAccentedBackgroundColor',
  },
  buttonColor: {
    mappedBrandKey: 'primaryColor',
  },
  inputLabelFont: {
    mappedBrandKey: 'secondaryFont',
  },
  inputFont: {
    mappedBrandKey: 'secondaryFont',
  },
  buttonFont: {
    mappedBrandKey: 'secondaryFont',
  },
  inputBorderColor: {
    mappedBrandKey: 'borderColor',
  },
  pageBackgroundColor: {
    mappedBrandKey: 'backgroundColor',
  },
  titleFontSize: {
    mappedBrandKey: 'subtitleFontSize',
  },
} as PageToBrandValuesMap<FormBrandedCustomizations>;

type OnTheFlyFormBrandedCustomizations = {
  dateTimeBackgroundColor: string;
  dateTimeTextColor: string;
  formBorder: string;
  pageBackgroundColor?: string;
  primaryButtonBackgroundColor: string;
  primaryButtonTextColor: string;
  primaryButtonBorderColor: string;
  primaryButtonHoverBackgroundColor: string;
  primaryButtonHoverTextColor: string;
  requiredIndicatorColor: string;
  inputTextColor: string;
  inputBorderColor: string;
  errorBorderColor: string;
  errorTextColor: string;
};

// These are the keys of the values inherited from the EventBrandValues set
// that we should exclude from the Form customizations
export const brandKeysExcludedFromFormCustomizations = [
  'backgroundColor',
  'backgroundGradientColor',
  'borderColor',
  'darkLogoBakeryHashedId',
  'darkLogoUrl',
  'formBorder',
  'lightLogoBakeryHashedId',
  'lightLogoUrl',
  'playerColor',
  'speakerTileBackgroundColor',
  'speakerTileBorder',
  'speakerTileTextColor',
  'speakerTileTitleTextColor',
  'speakerTileShadowColor',
  'speakerSocialStrokeColor',
  'speakerSocialFillColor',
  'secondaryButtonTextColor',
  'secondaryButtonBorderColor',
  'secondaryButtonHoverBackgroundColor',
  'secondaryButtonHoverTextColor',
  'brandDarkBackgroundColor',
  'brandLightBackgroundColor',
  'slightAccentedBackgroundColor',
  'slightAccentedTextColor',
  'slightAccentedShadowColor',
  'slightAccentedBorderColor',
  'heavyAccentedBackgroundColor',
  'heavyAccentedTextColor',
  'heavyAccentedBorderColor',
  'subtitleFontSize',
  'isDarkMode',
  'logoHeight',
  'logoTargetUrl',
  'shouldShowLogo',
] as const;

// These are the keys of the values that are generated on-the-fly
// for the form based on the inherited brand values and its existing customizations
export const generatedFormCustomizations = [
  'borderRadius',
  'buttonBorderRadius',
  'containerBorderRadius',
  'formBorder',
  'dateTimeBackgroundColor',
  'dateTimeTextColor',
  'headingTextColor',
  'inputBorderColor',
  'inputBorderRadius',
  'inputTextColor',
  'mainTextColor',
  'pageBackgroundColor',
  'primaryButtonBackgroundColor',
  'primaryButtonBorderColor',
  'primaryButtonHoverBackgroundColor',
  'primaryButtonHoverTextColor',
  'primaryButtonTextColor',
  'primaryColor',
  'primaryTextColor',
  'requiredIndicatorColor',
  'secondaryFont',
  'secondaryFontSize',
  'errorTextColor',
  'errorBorderColor',
];
